import { atom } from 'jotai'
import type { Timestamps } from '@rundown-studio/utils'
import { createTimestamps, fastDeepEqual } from '@rundown-studio/utils'
import { rundownAtom, filteredAndSortedCuesAtom } from './rundown.store'
import { captureException } from '../sentry'
import * as logger from '../utils/logger'
import { runnerAtom } from './runner.store'
import type { RundownCue } from '@rundown-studio/types'
import { nowAtom } from '@/shared/store/clock.store'

/**
 * Private value store
 */
const _timestamps = atom<Timestamps | null>(null)

/**
 * Public interface to timestamps
 * Getter: Get timestamps
 * Setter: Re-creates timestamps, takes no parameters
 */
export const timestampsAtom = atom(
  (get) => get(_timestamps),
  (get, set) => {
    // Collect params
    const cues = _parseCueDates(get(filteredAndSortedCuesAtom))
    const rundown = get(rundownAtom)
    const runner = get(runnerAtom)

    if (!Object.keys(cues).length || !rundown) {
      set(_timestamps, null)
      return
    }

    // Create timestamps
    const now = get(nowAtom)
    let newTimestamps: Timestamps | null = null
    try {
      newTimestamps = createTimestamps(
        cues,
        rundown.cues,
        runner,
        rundown.startTime instanceof Date
          ? rundown.startTime
          : rundown.startTime.toDate(),
        {
          timezone: rundown.timezone ?? undefined,
          now: new Date(now),
        },
      )
    } catch (err) {
      captureException(err)
      logger.debug('[createTimestamps] cues', cues)
      logger.debug('[createTimestamps] rundown.cues', rundown.cues)
      logger.debug('[createTimestamps] runner', runner)
      logger.debug('[createTimestamps] rundown.startTime', rundown.startTime)
    }

    // Preserve existing object signature if there are no changes
    const oldTimestamps = get(_timestamps)
    const isEqual = fastDeepEqual(newTimestamps, oldTimestamps)
    if (newTimestamps && !isEqual) set(_timestamps, newTimestamps)
  },
)

export const getTimestampByCueIdAtom = atom((get) => {
  const timestamps = get(_timestamps)

  return (cueId: string) => timestamps?.cues[cueId] ?? null
})

function _parseCueDates(cues: RundownCue[]): RundownCue[] {
  return cues.map((cue) => {
    if (cue.startTime) cue.startTime = new Date(cue.startTime)
    return cue
  })
}
